import { Subscription, CountryCode, SubscriptionAddOn, Variants, SubscriptionPlanCode, Order } from '@dayetopia/types';
import { PPC_TAMPON_PACK_COUNT, PROVIOTICS_TABLETS_COUNT } from '@constants';
import { BadgeType, ONE_MONTH_INTERVAL_LENGTH, BoxState, ProductCode, TFunction, Timescale } from '@contracts';
import ContraceptionImage from '@images/purchases/sub-products/contraception-product.png';
import DTImage from '@images/purchases/sub-products/dt-product.png';
import PadsImage from '@images/purchases/sub-products/pads-product.png';
import ProvioticsImage from '@images/purchases/sub-products/proviotics-product.png';
import TamponsImage from '@images/purchases/sub-products/tampons-product.png';
import { getProductIdFromPlanCode } from '@utils/account';
import { getDeliveryDatesText } from '@utils/getDeliveryDatesText';
import { getAllPadTypes, getCurrentPadsCount, isPadsStackBox } from '@utils/pads';
import { getCurrentTamponsCount, tamponBoxContainsCBDTampons } from '@utils/tampons';
import { getBundleVariant } from './bundles';

export enum DissuasionStep {
  none = 0,

  TamponsInitialScreen = 1,
  TamponsTooExpensive = 2,
  TamponsTooMany = 3,
  TamponsNoPeriod = 4,
  TamponsDifferentCare = 5,
  TamponsDontWork = 6,
  TamponsOpenText = 7,
  TamponsTooManyOffer = 8,
  TamponsPregnant = 9,
  TamponsUnpredictablePeriod = 10,
  TamponsNoPeriodMissYou = 11,
  TamponsDifferentCareOffer = 12,
  TamponsDifferentCareMissYou = 13,
  TamponsNoPeriodOpenText = 33,
  TamponsDifferentCareOpenText = 34,
  TamponsNoPeriodOpenTextOffer = 35,
  TamponsDifferentCareOpenTextOffer = 36,
  TamponsDontWorkOpenText = 14,
  TamponsOpenTextOffer = 15,
  TamponsPregnantMissYou = 16,
  TamponsDontWorkOpenTextOffer = 17,
  TamponsEnd = 18,

  ProvioticsInitialScreen = 19,
  ProvioticsSpecialOffer = 20,
  ProvioticsTooMany = 21,
  ProvioticsHealthImproved = 22,
  ProvioticsOpenText = 23,
  ProvioticsOpenTextOffer = 24,
  ProvioticsEnd = 25,

  VmsInitialScreen = 26,
  VmsSpecialOffer = 27,
  VmsInfectionCleared = 28,
  VmsOpenText = 29,
  VmsOpenTextOffer = 30,
  VmsEnd = 31,

  PadsInitialScreen = 32
}

export const getSubscriptionPlanCode = (subscription: Subscription) => subscription.pendingChange?.plan?.code ?? subscription.planCode;

export const getSubscriptionPauseCycles = (subscription: Subscription) =>
  subscription.remainingPauseCycles && subscription.remainingPauseCycles > 0 ? subscription.remainingPauseCycles : 0;

export const subscriptionIsPaused = (subscription: Subscription) =>
  subscription.state === 'paused' || !!(getSubscriptionPauseCycles(subscription) > 0);

export const subscriptionIsExpired = (subscription: Subscription) => subscription.state === 'expired' || subscription.state === 'canceled';

export const subscriptionIsActive = (subscription: Subscription) => {
  return subscription.state === 'active' && !subscriptionIsPaused(subscription);
};
export const subscriptionIsNotPaid = (subscription: Subscription) =>
  subscription.state === 'expired' && subscription.expirationReason === 'Non-payment';

export const subscriptionCanBeCustomised = (subscription: Subscription) => subscription.state === 'expired';

export const isMultiMonth = (planCode: any) => planCode.includes('3_month') || planCode.includes('6_month') || planCode.includes('12_month');

export const isUpfrontPayment = (planCode: any) => planCode.includes('upfront');

export const getActiveSubscriptions = (subscriptions: Subscription[]) => {
  return subscriptions.filter((subscription) => subscription.state === 'active' && !subscriptionIsPaused(subscription));
};

export const getPausedSubscriptions = (subscriptions: Subscription[]) => {
  return subscriptions.filter((subscription) => subscriptionIsPaused(subscription));
};

export const getExpiredSubscriptions = (subscriptions: Subscription[]) => {
  return subscriptions.filter((subscription) => subscriptionIsExpired(subscription));
};

export const getSubscriptionState = (subscription: Subscription) => {
  return subscriptionIsPaused(subscription) ? 'paused' : subscription.state;
};

export const getSubscriptionBadgeText = (subscription: Subscription) => {
  return subscriptionIsPaused(subscription) ? 'paused by you' : subscription.state;
};

export const getSubscriptionBadgeType = (subscription: Subscription): BadgeType => {
  if (subscriptionIsPaused(subscription)) {
    return 'recommended';
  }

  if (subscriptionIsExpired(subscription)) {
    return 'negative';
  }

  return 'positive';
};

export const getSubscriptionHeadingBackground = (subscription: Subscription): string => {
  if (subscriptionIsPaused(subscription)) {
    return 'linear-gradient(180deg, #FCF2DE 0%, rgba(252, 242, 222, 0.30) 100%)';
  }

  if (subscriptionIsExpired(subscription)) {
    return 'linear-gradient(180deg, #FCE5D3 0%, rgba(252, 229, 211, 0.30) 100%)';
  }

  return 'linear-gradient(180deg, #EBF5EA 0%, rgba(235, 245, 234, 0.30) 100%)';
};

const convertAddOnsToVariants = (items: SubscriptionAddOn[]) => {
  const addOnVariants: Variants = {};
  items.forEach((item: SubscriptionAddOn) => {
    addOnVariants[item.addOn.code] = item.quantity;
  });
  return addOnVariants;
};

export const getVariantsFromSubscription = (subscription: Subscription, isPadSubscription: boolean, country: CountryCode) => {
  const { variants, addOns, pendingChange } = subscription;

  const pendingAddOns = pendingChange && pendingChange.addOns ? pendingChange.addOns : addOns;
  const pendingVariants =
    pendingChange && pendingChange.customFields ? pendingChange.customFields.find((field: any) => field.name === 'variants') : null;
  let returnVariants: Variants = {};

  if (pendingAddOns && pendingAddOns.length > 0) {
    returnVariants = convertAddOnsToVariants(pendingAddOns);
  } else if (pendingVariants || variants) {
    returnVariants = pendingVariants ? JSON.parse(pendingVariants.value) : variants;
  }

  if (isPadsStackBox(country)) {
    const nonStackBoxVariants = getAllPadTypes();

    nonStackBoxVariants.forEach((variant) => {
      delete returnVariants[variant];
    });
  }

  if (returnVariants) {
    returnVariants.boxSize = isPadSubscription ? getCurrentPadsCount(returnVariants, country) : getCurrentTamponsCount(returnVariants);
  }

  return returnVariants;
};

export const getSubscriptionVariantsCount = (subscription: Subscription) => {
  const { variants } = subscription;

  if (Array.isArray(variants)) {
    return variants.reduce((acc, curr) => acc + curr.count, 0);
  }

  if (typeof variants === 'object') {
    const { boxSize } = variants as BoxState;

    if (boxSize) {
      return boxSize;
    }

    return Object.values(variants).reduce((acc, curr) => acc + curr, 0);
  }

  return 0;
};

export const getSubscriptionVariantsCountText = (subscription: Subscription, t: TFunction) => {
  const planCode = getSubscriptionPlanCode(subscription);
  const count = getSubscriptionVariantsCount(subscription);

  if (count > 0) {
    if (planCode?.includes('tampon_')) {
      if (planCode?.includes('ppc_tampon_')) {
        return t('components.n-pack', { n: PPC_TAMPON_PACK_COUNT });
      }

      return count > 1 ? t('components.n-pack', { n: count }) : '';
    }

    if (planCode?.includes('proviotics')) {
      const months = getMonthsFromPlanCode(planCode);
      return `${t('components.n-tablets', { n: months * PROVIOTICS_TABLETS_COUNT })}`;
    }
  }

  return '';
};

export const getSubscriptionImage = (subscription: Subscription) => {
  const { planCode } = subscription;

  if (planCode?.includes('ppc_tampon_')) {
    return TamponsImage;
  }

  if (planCode?.includes('tampon_')) {
    return TamponsImage;
  }

  if (planCode?.includes('proviotics')) {
    return ProvioticsImage;
  }

  if (planCode?.includes('pads')) {
    return PadsImage;
  }

  if (planCode?.includes('contraception')) {
    return ContraceptionImage;
  }

  if (planCode?.includes('screening_kit')) {
    return DTImage;
  }

  return null;
};

export const getSubscriptionInfoTexts = (subscription: Subscription, t: TFunction) => {
  const subscriptionFrequency = getSubscriptionFrequency(subscription, t);
  const variantsCount = getSubscriptionVariantsCountText(subscription, t);
  const infoTexts: string[] = [];

  infoTexts.push(subscriptionFrequency);

  if (variantsCount) {
    infoTexts.push(variantsCount);
  }

  return infoTexts;
};

export const getMonthsFromPlanCode = (planCode: string | undefined | null) => {
  if (!planCode) {
    return 0;
  }

  let months = 0;

  if (planCode.endsWith('_month')) {
    const planCodeParts = planCode.split('_');
    const monthPart = planCodeParts[planCodeParts.length - 2];

    if (monthPart) {
      months = Number(monthPart);
    }
  } else if (planCode.endsWith('_month_upfront')) {
    const planCodeParts = planCode.split('_');
    const monthPart = planCodeParts[planCodeParts.length - 3];

    if (monthPart) {
      months = Number(monthPart);
    }
  }
  return months;
};

const getSubscriptionFrequency = (subscription: Subscription, t: TFunction) => {
  const planCode = getSubscriptionPlanCode(subscription);
  const months = getMonthsFromPlanCode(planCode) || subscription.planLength;

  if (months === ONE_MONTH_INTERVAL_LENGTH) {
    return t('components.subscription.every-month');
  }

  return `${t('components.subscription.every-n-months', { n: months })}`;
};

export const getProductIdFromSubscription = (subscription: Subscription | undefined) => {
  if (!subscription) {
    return '';
  }

  const bundlePlanCode = getBundleVariant(subscription?.variants);
  const productId = getProductIdFromPlanCode(bundlePlanCode || subscription?.planCode);

  return productId;
};

export const getSubscriptionBufferDays = (subscription: Subscription) => {
  return subscription.shipping?.address?.country === CountryCode.GB ? 7 : 12;
};

export const getSubscriptionDeliveryDatesText = (
  subscription: Subscription,
  latestOrder: Order | null | undefined,
  currentOrder: Order | null | undefined,
  timescale: Timescale
) => {
  const date = getSubscriptionMainDate(subscription, latestOrder, currentOrder);

  return date ? getDeliveryDatesText(date, timescale, true) : '';
};

export const hasNextDelivery = (subscription: Subscription) => {
  const isSubscriptionExpired = subscriptionIsExpired(subscription);
  const nextBillDate = subscription.futureBillingDate ? new Date(subscription.futureBillingDate) : null;
  const termEndsDate = subscription.currentTermEndsAt ? new Date(subscription.currentTermEndsAt) : null;

  if (!nextBillDate || !termEndsDate) {
    return false;
  }

  return !isSubscriptionExpired || nextBillDate.getTime() !== termEndsDate.getTime();
};

export const isNextDeliveryDateShown = (subscription: Subscription) => {
  return subscriptionIsActive(subscription) && hasNextDelivery(subscription);
};

export const getSubscriptionMainDate = (
  subscription: Subscription,
  latestOrder: Order | null | undefined,
  currentOrder: Order | null | undefined
) => {
  if (isNextDeliveryDateShown(subscription)) {
    return subscription.futureBillingDate ? new Date(subscription.futureBillingDate) : null;
  }

  const lastOrder = currentOrder || latestOrder;

  const date = new Date(lastOrder ? lastOrder.timestamp * 1000 : subscription.futureBillingDate || 0);

  return date;
};

export const getSubscriptionMainDateText = (
  subscription: Subscription,
  latestOrder: Order | null | undefined,
  currentOrder: Order | null | undefined
) => {
  const billingDate = getSubscriptionMainDate(subscription, latestOrder, currentOrder);

  if (!billingDate) {
    return '';
  }

  return new Date(billingDate).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
};

export const subscriptionIsPrescription = (subscription: Subscription | undefined | null) => {
  if (!subscription) {
    return false;
  }

  const planCode = getSubscriptionPlanCode(subscription);

  return planCode === SubscriptionPlanCode.PPCTamponBox3Month;
};

export const filterSubscriptionOrders = (orders: Order[], subscription: Subscription | null | undefined) => {
  if (!subscription) return [];

  const planCode = getSubscriptionPlanCode(subscription);
  const productCode = getProductIdFromPlanCode(planCode);

  return orders.filter((order: Order) => order.purchaseItems.find((item) => item.variant === productCode));
};

/**
 * Checks if a subscription can be reactivated
 *
 * @param subscription The subscription to check
 * @returns boolean indicating if the subscription can be reactivated
 */
export const canReactivateSubscription = (subscription: Subscription | undefined | null): boolean => {
  if (!subscription) return false;

  const productId = getProductIdFromPlanCode(subscription.planCode ?? '');

  // If no productId, don't allow reactivation
  if (!productId) {
    return false;
  }

  // Don't allow reactivation of CBD subscriptions in the US
  if (subscription.variants) {
    // Get subscription's shipping country rather than user's current country
    const shippingCountry = subscription.shipping?.address?.country;
    const variantNames = Object.keys(subscription.variants);
    const containsCBD = tamponBoxContainsCBDTampons(productId as ProductCode, variantNames);

    if (containsCBD && shippingCountry === CountryCode.US) {
      return false;
    }
  }

  return true;
};

/**
 * Checks if a subscription can be edited based on its state
 *
 * @param subscription The subscription to check
 * @returns boolean indicating if the subscription can be edited
 */
export const canEditSubscription = (subscription: Subscription | undefined | null): boolean => {
  if (!subscription) return false;

  return !subscriptionIsExpired(subscription) || (subscriptionIsExpired(subscription) && canReactivateSubscription(subscription));
};
